import { useEffect, useRef, useState } from 'react';
import { closeSidePanel, getEventBus, openSidePanel } from '@alfa-bank/newclick-core/window-utils';
import { type EventMap } from '@alfa-bank/newclick-types';
import type { OpenSidePanelsParams } from '@alfa-bank/newclick-types/open-side-panel-params';
import { VoipOptions } from '@alfa-bank/newclick-types/open-side-panel-params/options/voip';
import { Page } from '@alfa-bank/newclick-voip-side-panel-widget/types/constants';

import { useChat } from '#/src/shared/components/alternative-login/modals/components/modal-chat/hooks/use-chat';
import { history } from '#/src/shared/history';
import { useAppSelector } from '#/src/shared/hooks';
import { getQueryRedirectParams } from '#/src/shared/store/redux/app/selectors';

import { phoneNumberDefault } from '../constants';

export const useVoipChanges = () => {
    const eventBus = getEventBus();
    const queryRedirectParams = useAppSelector(getQueryRedirectParams);
    const { openChat } = useChat();

    const [showVoip, setShowVoip] = useState(false);

    const [voipOptions, setVoipOptions] = useState<VoipOptions | null>(null);
    const prevSidePanel = useRef<string | null>(null);

    const handleBack = (calling?: boolean) => {
        if (calling === true) {
            eventBus?.emit('CHANGE_VOIP_STATE' as keyof EventMap, {
                open: true,
                options: {
                    view: Page.Voip,
                },
            });

            openSidePanel({
                sidePanelId: prevSidePanel.current ?? 'Connections',
            } as unknown as OpenSidePanelsParams);

            return;
        }
        eventBus?.emit('CHANGE_VOIP_STATE' as keyof EventMap, {
            open: false,
        });

        if (prevSidePanel.current) {
            window.isVoipOpen = false;
            if (prevSidePanel.current === 'Chat') {
                openChat();

                return;
            }

            openSidePanel({ sidePanelId: prevSidePanel.current } as OpenSidePanelsParams);
        }
    };

    useEffect(() => {
        if (eventBus && queryRedirectParams.sidepanelid === 'voip') {
            const options: VoipOptions = {
                view: 'voip',
                immediatelyStartCall: true,
                phoneNumber: queryRedirectParams.number || phoneNumberDefault,
            };

            setVoipOptions(options);
            setShowVoip(true);

            history.replace(window.location.href.replace('&sidepanelid=voip', ''));
        }
    }, [eventBus, queryRedirectParams.number, queryRedirectParams.sidepanelid]);

    useEffect(() => {
        const handleChangeVoipState = ({
            open,
            options,
        }: {
            open: boolean;
            options: VoipOptions;
        }) => {
            if (options) {
                setVoipOptions(options);
            }

            if (open) {
                closeSidePanel();
            }

            setShowVoip(open);
        };

        const handleChangeSidePanelState = ({
            sidePanelId,
        }: EventMap['CHANGE_SIDE_PANEL_STATE']) => {
            if (sidePanelId) {
                prevSidePanel.current = sidePanelId;
            }
        };

        eventBus?.subscribe('CHANGE_VOIP_STATE' as keyof EventMap, handleChangeVoipState);
        eventBus?.subscribe('CHANGE_SIDE_PANEL_STATE', handleChangeSidePanelState);

        return () => {
            eventBus?.unsubscribe('CHANGE_VOIP_STATE' as keyof EventMap, handleChangeVoipState);
            eventBus?.subscribe('CHANGE_SIDE_PANEL_STATE', handleChangeSidePanelState);
        };
    }, [eventBus]);

    if (typeof window !== 'undefined') {
        window.isVoipOpen = showVoip;
    }

    return {
        showVoip,
        handleBack,
        options: voipOptions,
    };
};
